/**
 * ImportCustomersDialog — the "Import CSV" flow.
 *
 * Three internal steps:
 *   1. Pick — file picker + template-download link.
 *   2. Preview — table of parsed rows, each tagged valid / duplicate /
 *      validation-error. Non-valid rows are dimmed but never sent.
 *   3. Submitting — mutation runs; on success we toast + invalidate the
 *      list / counts caches so the page refreshes with the new rows.
 *
 * The parser (papaparse) and validator (`importRow` Zod schema) both run
 * client-side so the user sees errors before pressing Import. The server
 * re-validates for the tampered-client case and does its own duplicate
 * check against existing customers.
 *
 * One row = one customer, so this flow is flat: no `source-ref` grouping and
 * no column-mapping step. It composes the shared pieces from
 * `components/csv-import/` rather than using `GroupedImportDialog`.
 */
import { useMemo, useRef, useState } from "react";
import { Download } from "lucide-react";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@flint/ui";
import { customers as contract } from "@flint/api-contract";
import {
  FilePickStep,
  ImportResultPanel,
  parseCsv,
  RowStatusBadge,
} from "../../../components/csv-import/index.ts";
import { trpc } from "../../../lib/trpc.ts";

type ImportRow = contract.ImportRow;

const MAX_ROWS = 500;

// Kebab-case CSV header → camelCase domain field.
const HEADER_MAP: Record<string, string> = {
  name: "name",
  "contact-name": "contactName",
  gstin: "gstin",
  phone: "phone",
  "phone-secondary": "phoneSecondary",
  email: "email",
  "address-line-1": "addressLine1",
  "address-line-2": "addressLine2",
  state: "state",
  city: "city",
  pincode: "pincode",
  country: "country",
  "payment-terms": "paymentTerms",
  "credit-limit-rupees": "_creditLimitRupees",
  "opening-balance-rupees": "_openingBalanceRupees",
  "opening-balance-as-of": "openingBalanceAsOf",
  "is-tds-applicable": "isTdsApplicable",
  "tds-section": "tdsSection",
};

const TEMPLATE_HEADERS = Object.keys(HEADER_MAP);

type RowStatus = "valid" | "duplicate_gstin" | "validation";

interface PreviewRow {
  index: number;
  raw: Record<string, string>;
  parsed: ImportRow | null;
  status: RowStatus;
  message: string;
}

interface ImportCustomersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Fires after a successful batch import finishes AND the list/counts
   * cache invalidations resolve. Callers can navigate the user somewhere
   * more useful (the customers list, a dashboard) instead of leaving them
   * on the surface that mounted the dialog. Called with `{ imported,
   * skipped }` so the caller can gate its behavior on how well the import
   * went. Optional — if omitted, the dialog just shows the result state
   * and waits for the user to close it manually.
   */
  onImported?: (result: { imported: number; skipped: number }) => void;
}

export function ImportCustomersDialog({
  open,
  onOpenChange,
  onImported,
}: ImportCustomersDialogProps) {
  const [step, setStep] = useState<"pick" | "preview">("pick");
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const utils = trpc.useUtils();
  const importMutation = trpc.customers.import.useMutation();

  // Existing customers' GSTINs — used for the client-side duplicate hint.
  // Server re-checks on submit, so this is best-effort UX guidance.
  const existingList = trpc.customers.list.useQuery({ limit: 100, tab: "all" }, { enabled: open });
  const existingGstins = useMemo(() => {
    const set = new Set<string>();
    for (const r of existingList.data?.items ?? []) {
      if (r.gstin) set.add(r.gstin.toUpperCase());
    }
    return set;
  }, [existingList.data]);

  const validRows = useMemo(() => rows.filter((r) => r.status === "valid"), [rows]);
  const skippedCount = rows.length - validRows.length;

  function reset() {
    setStep("pick");
    setRows([]);
    setFileName("");
    setParseError(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  function handleClose(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  async function handleFile(file: File) {
    setFileName(file.name);
    setParseError(null);
    try {
      const data = await parseCsv(file);
      if (data.length === 0) {
        setParseError("The file has no rows. Add at least one customer under the header row.");
        return;
      }
      if (data.length > MAX_ROWS) {
        setParseError(`File exceeds the ${MAX_ROWS}-row limit. Split it into smaller files.`);
        return;
      }
      const seenGstins = new Set<string>();
      setRows(data.map((raw, i) => classifyRow(raw, i, existingGstins, seenGstins)));
      setStep("preview");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setParseError(`Could not parse the file: ${msg}`);
    }
  }

  async function handleImport() {
    if (validRows.length === 0) return;
    const payload = validRows.map((r) => r.parsed).filter((r): r is ImportRow => r !== null);
    const res = await importMutation.mutateAsync({ rows: payload });
    const summary = {
      imported: res.imported,
      skipped: res.skipped.length + skippedCount,
    };
    setResult(summary);
    await Promise.all([utils.customers.list.invalidate(), utils.customers.counts.invalidate()]);
    onImported?.(summary);
  }

  return (
    <DialogRoot open={open} onOpenChange={handleClose}>
      <DialogContent size="lg" className="!max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import customers from CSV</DialogTitle>
          <DialogDescription>
            Up to {MAX_ROWS} rows per file. Duplicate GSTINs and validation errors are skipped —
            valid rows still land.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <ImportResultPanel summary={result} skipped={importMutation.data?.skipped} />
        ) : step === "pick" ? (
          <FilePickStep
            inputId="import-csv-file"
            accept=".csv,text/csv"
            label="Choose a CSV file"
            hint="Header row required. UTF-8 encoded."
            parseError={parseError}
            inputRef={inputRef}
            onFile={(file) => void handleFile(file)}
          >
            <a
              href={`data:text/csv;charset=utf-8,${encodeURIComponent(TEMPLATE_HEADERS.join(","))}`}
              download="customers-import-template.csv"
              className="inline-flex items-center gap-1.5 text-xs text-sky-600 hover:underline"
            >
              <Download size={12} aria-hidden />
              Download template
            </a>
          </FilePickStep>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="text-xs text-muted">
              <strong className="text-ink">{fileName}</strong> · {rows.length} row
              {rows.length === 1 ? "" : "s"} ·{" "}
              <span className="text-ok">{validRows.length} ready</span>
              {skippedCount > 0 ? (
                <>
                  {" · "}
                  <span className="text-warn">{skippedCount} to skip</span>
                </>
              ) : null}
            </div>
            <div className="max-h-72 overflow-auto rounded-sm border border-line">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-bg text-muted">
                  <tr>
                    <th className="px-2 py-1 text-left w-8">#</th>
                    <th className="px-2 py-1 text-left">Name</th>
                    <th className="px-2 py-1 text-left">GSTIN</th>
                    <th className="px-2 py-1 text-left">City</th>
                    <th className="px-2 py-1 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 100).map((r) => (
                    <tr
                      key={r.index}
                      className={`border-t border-line ${r.status !== "valid" ? "opacity-60" : ""}`}
                    >
                      <td className="px-2 py-1 text-muted">{r.index + 2}</td>
                      <td className="px-2 py-1">
                        {r.raw.name || <span className="text-muted">—</span>}
                      </td>
                      <td className="px-2 py-1 font-mono text-xs">{r.raw.gstin || "—"}</td>
                      <td className="px-2 py-1">{r.raw.city || "—"}</td>
                      <td className="px-2 py-1">
                        <RowStatusBadge
                          tone={
                            r.status === "valid"
                              ? "valid"
                              : r.status === "duplicate_gstin"
                                ? "duplicate"
                                : "invalid"
                          }
                          duplicateLabel="Duplicate GSTIN"
                          message={r.message}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 100 ? (
                <p className="px-2 py-1 text-xs text-muted">
                  Showing first 100 of {rows.length} rows.
                </p>
              ) : null}
            </div>
          </div>
        )}

        <DialogFooter className="mt-5">
          {result ? (
            <button
              type="button"
              onClick={() => handleClose(false)}
              className="inline-flex h-9 items-center rounded-sm bg-ink px-4 text-sm font-medium text-paper hover:bg-ink-2"
            >
              Done
            </button>
          ) : step === "pick" ? (
            <button
              type="button"
              onClick={() => handleClose(false)}
              className="inline-flex h-9 items-center rounded-sm border border-line bg-paper px-4 text-sm font-medium hover:bg-bg"
            >
              Cancel
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={reset}
                disabled={importMutation.isPending}
                className="inline-flex h-9 items-center rounded-sm border border-line bg-paper px-4 text-sm font-medium hover:bg-bg disabled:cursor-not-allowed disabled:opacity-60"
              >
                Choose different file
              </button>
              <button
                type="button"
                onClick={() => void handleImport()}
                disabled={validRows.length === 0 || importMutation.isPending}
                className="inline-flex h-9 items-center rounded-sm bg-ink px-4 text-sm font-medium text-paper hover:bg-ink-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {importMutation.isPending
                  ? "Importing…"
                  : `Import ${validRows.length} customer${validRows.length === 1 ? "" : "s"}`}
              </button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}

// ── helpers ────────────────────────────────────────────────────────

function classifyRow(
  raw: Record<string, string>,
  index: number,
  existingGstins: Set<string>,
  seenGstinsInFile: Set<string>,
): PreviewRow {
  const mapped: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    const field = HEADER_MAP[key];
    if (!field) continue;
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (!trimmed) continue;
    mapped[field] = trimmed;
  }

  // Money — CSV expresses rupees, domain expects paise (stringified bigint).
  const creditRupees = mapped._creditLimitRupees as string | undefined;
  if (creditRupees !== undefined) {
    const paise = rupeesToPaise(creditRupees);
    if (paise !== null) mapped.creditLimitPaise = paise;
    delete mapped._creditLimitRupees;
  }
  const openingRupees = mapped._openingBalanceRupees as string | undefined;
  if (openingRupees !== undefined) {
    const paise = rupeesToPaise(openingRupees);
    if (paise !== null) mapped.openingBalancePaise = paise;
    delete mapped._openingBalanceRupees;
  }
  // Boolean — CSV expresses yes/no/true/false.
  if (typeof mapped.isTdsApplicable === "string") {
    const v = (mapped.isTdsApplicable as string).toLowerCase();
    mapped.isTdsApplicable = v === "yes" || v === "true" || v === "1";
  }
  // GSTIN — normalise to uppercase since the validator is fixed-case.
  if (typeof mapped.gstin === "string") {
    mapped.gstin = (mapped.gstin as string).toUpperCase();
  }

  // Duplicate check — inside file OR against workspace.
  const gstinUpper = typeof mapped.gstin === "string" ? (mapped.gstin as string) : null;
  if (gstinUpper) {
    if (seenGstinsInFile.has(gstinUpper)) {
      return {
        index,
        raw,
        parsed: null,
        status: "duplicate_gstin",
        message: `GSTIN ${gstinUpper} appears more than once in this file.`,
      };
    }
    if (existingGstins.has(gstinUpper)) {
      return {
        index,
        raw,
        parsed: null,
        status: "duplicate_gstin",
        message: `GSTIN ${gstinUpper} already exists in this workspace.`,
      };
    }
    seenGstinsInFile.add(gstinUpper);
  }

  const parseResult = contract.importRow.safeParse(mapped);
  if (!parseResult.success) {
    const issue = parseResult.error.issues[0];
    return {
      index,
      raw,
      parsed: null,
      status: "validation",
      message: issue
        ? `${issue.path.join(".") || "row"}: ${issue.message}`
        : "Row failed validation.",
    };
  }

  return { index, raw, parsed: parseResult.data, status: "valid", message: "Ready to import." };
}

function rupeesToPaise(input: string): string | null {
  const stripped = input.replace(/,/g, "").trim();
  const n = Number(stripped);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100).toString();
}
